import { CircleAlert, TriangleAlert } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useSearchParams } from "react-router";
import { BudgetBar } from "@/components/BudgetProgress";
import { CategoryIcon } from "@/components/CategoryIcon";
import { Button } from "@/components/ui/button";
import { Card, PageHeader } from "@/components/ui/card";
import { EmptyState, ErrorBanner, Loading } from "@/components/ui/feedback";
import { Select } from "@/components/ui/field";
import { budgetLevel } from "@/lib/dashboard";
import { cn } from "@/lib/utils";
import {
  useBudgetStatus,
  useCategories,
  useDeleteBudget,
  useMonthlySummary,
  useSetBudget,
} from "../../api/finance";
import type { BudgetStatus } from "../../api/types";
import { MoneyInput } from "../../components/MoneyInput";
import { useCan, useWorkspace } from "../../layout/workspace-context";
import { addMonths, currentMonth, formatMonth, isMonth } from "../../lib/dates";
import { formatMoney, formatPercent, parseMoneyInput } from "../../lib/money";

function statusText(percent: number) {
  const level = budgetLevel(percent);
  if (level === "over") return `Estourado: ${formatPercent(percent)} usado`;
  if (level === "warning") return `Atenção: ${formatPercent(percent)} usado`;
  return `${formatPercent(percent)} usado`;
}

export function BudgetsPage() {
  const ws = useWorkspace();
  const canEdit = useCan("catalog:write");
  const [params, setParams] = useSearchParams();
  const month = isMonth(params.get("month") ?? "")
    ? (params.get("month") as string)
    : currentMonth(ws.timezone);
  const status = useBudgetStatus(ws.id, month);
  const summary = useMonthlySummary(ws.id, month);
  const foreign =
    summary.data?.otherCurrencies.filter((o) => o.expenseMinor > 0).map((o) => o.currency) ?? [];
  // O limite está na moeda do workspace; a API deixa de fora as despesas em outras moedas (#19).
  const foreignNote = foreign.length
    ? `Despesas em ${new Intl.ListFormat("pt-BR", { type: "conjunction" }).format(foreign)} não entram nos orçamentos.`
    : undefined;
  const remove = useDeleteBudget(ws.id);
  const goTo = (m: string) => setParams({ month: m }, { replace: true });

  return (
    <>
      <PageHeader
        title={`Orçamentos — ${formatMonth(month)}`}
        actions={
          <>
            <Button variant="secondary" onClick={() => goTo(addMonths(month, -1))}>
              Mês anterior
            </Button>
            <Button variant="secondary" onClick={() => goTo(addMonths(month, 1))}>
              Próximo mês
            </Button>
          </>
        }
      />
      {status.isPending ? (
        <Loading />
      ) : status.isError ? (
        <ErrorBanner error={status.error} />
      ) : status.data.length === 0 ? (
        <EmptyState title="Nenhum orçamento definido.">{foreignNote}</EmptyState>
      ) : (
        <>
          <Summary budgets={status.data} currency={ws.defaultCurrency} note={foreignNote} />
          <ul
            aria-label="Orçamentos"
            className="m-0 mb-8 grid list-none gap-4 p-0 md:grid-cols-2 xl:grid-cols-3"
          >
            {status.data.map((b) => (
              <BudgetRow
                key={b.categoryId}
                budget={b}
                currency={ws.defaultCurrency}
                canEdit={canEdit}
                onRemove={() => remove.mutate(b.categoryId)}
              />
            ))}
          </ul>
        </>
      )}
      <ErrorBanner error={remove.error} />
      {canEdit && <BudgetForm />}
    </>
  );
}

/** Resumo do mês: totais = soma dos cards (o restante pode ficar negativo quando o gasto passa do orçado). */
function Summary({ budgets, currency, note }: { budgets: BudgetStatus[]; currency: string; note?: string }) {
  const budgeted = budgets.reduce((s, b) => s + b.budgetedMinor, 0);
  const spent = budgets.reduce((s, b) => s + b.spentMinor, 0);
  const remaining = budgeted - spent;
  const percent = budgeted > 0 ? (spent / budgeted) * 100 : 0;
  const over = budgets.filter((b) => budgetLevel(b.percentUsed) === "over").length;
  const warning = budgets.filter((b) => budgetLevel(b.percentUsed) === "warning").length;
  return (
    <Card title="Resumo do mês" note={note} className="mb-6">
      <dl className="m-0 mb-4 flex flex-col gap-2 sm:grid sm:grid-cols-3 sm:gap-3">
        {(
          [
            ["Orçado", budgeted],
            ["Gasto", spent],
            [remaining >= 0 ? "Restante" : "Acima", Math.abs(remaining)],
          ] as const
        ).map(([label, value]) => (
          <div key={label} className="flex min-w-0 items-baseline justify-between gap-3 sm:block">
            <dt className="text-xs font-medium text-muted">{label}</dt>
            <dd
              className={cn(
                "m-0 text-base font-semibold tabular-nums sm:truncate sm:text-lg lg:text-xl",
                label === "Acima" && "text-expense",
              )}
            >
              {formatMoney(value, currency)}
            </dd>
          </div>
        ))}
      </dl>
      <BudgetBar label="Total do mês" percent={percent} level={budgetLevel(percent)} />
      {(over > 0 || warning > 0) && (
        <p className="m-0 mt-3 flex flex-wrap gap-2 text-xs font-semibold">
          {over > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-expense-soft px-2 py-0.5 text-expense">
              <CircleAlert aria-hidden="true" className="size-3" />
              {over} {over === 1 ? "estourado" : "estourados"}
            </span>
          )}
          {warning > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-warning-soft px-2 py-0.5 text-warning">
              <TriangleAlert aria-hidden="true" className="size-3" />
              {warning} em atenção
            </span>
          )}
        </p>
      )}
    </Card>
  );
}

function BudgetRow({
  budget,
  currency,
  canEdit,
  onRemove,
}: {
  budget: BudgetStatus;
  currency: string;
  canEdit: boolean;
  onRemove: () => void;
}) {
  const over = budget.remainingMinor < 0;
  const level = budgetLevel(budget.percentUsed);
  return (
    <li
      aria-label={budget.categoryName}
      className="flex min-w-0 flex-col gap-3 rounded-card bg-surface p-5 shadow-card"
    >
      <div className="flex items-center gap-3">
        <CategoryIcon category={{ id: budget.categoryId, name: budget.categoryName }} />
        <div className="min-w-0 flex-1">
          <p className="m-0 truncate font-semibold">{budget.categoryName}</p>
          <p className="m-0 text-sm tabular-nums text-muted">
            {formatMoney(budget.spentMinor, currency)} de {formatMoney(budget.budgetedMinor, currency)}
          </p>
        </div>
      </div>
      <BudgetBar label={budget.categoryName} percent={budget.percentUsed} level={level} />
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span
          className={cn(
            "inline-flex items-center gap-1 font-medium",
            level === "over" ? "text-expense" : level === "warning" ? "text-warning" : "text-muted",
          )}
        >
          {level === "over" && <CircleAlert aria-hidden="true" className="size-3.5" />}
          {level === "warning" && <TriangleAlert aria-hidden="true" className="size-3.5" />}
          {statusText(budget.percentUsed)}
        </span>
        <span className="tabular-nums">
          {over
            ? `Excedeu ${formatMoney(-budget.remainingMinor, currency)}`
            : `Restam ${formatMoney(budget.remainingMinor, currency)}`}
        </span>
      </div>
      {canEdit && (
        <Button
          variant="ghost"
          className="-mb-2 self-start"
          aria-label={`Remover orçamento de ${budget.categoryName}`}
          onClick={onRemove}
        >
          Remover
        </Button>
      )}
    </li>
  );
}

function BudgetForm() {
  const ws = useWorkspace();
  const categories = useCategories(ws.id);
  const save = useSetBudget(ws.id);
  const [categoryId, setCategoryId] = useState("");
  const [limit, setLimit] = useState("");
  const [invalid, setInvalid] = useState(false);
  const expense = (categories.data ?? []).filter((c) => c.kind === "expense" && !c.archivedAt);
  const ordered = expense
    .filter((c) => !c.parentId)
    .flatMap((r) => [r, ...expense.filter((c) => c.parentId === r.id)]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const monthlyLimitMinor = parseMoneyInput(limit);
    if (monthlyLimitMinor === null || !categoryId) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    await save.mutateAsync({ categoryId, monthlyLimitMinor });
    setLimit("");
  }

  return (
    <Card title="Definir orçamento" className="max-w-xl">
      <form onSubmit={(e) => void submit(e).catch(() => undefined)} className="flex flex-col gap-4">
        <Select label="Categoria" required value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
          <option value="">Escolha a categoria de despesa</option>
          {ordered.map((c) => (
            <option key={c.id} value={c.id}>
              {c.parentId ? `— ${c.name}` : c.name}
            </option>
          ))}
        </Select>
        <MoneyInput label="Limite mensal" required value={limit} onChange={(e) => setLimit(e.target.value)} />
        {invalid && (
          <p className="m-0 text-sm text-expense">Escolha a categoria e informe um valor válido.</p>
        )}
        <ErrorBanner error={save.error} />
        <Button type="submit" disabled={save.isPending}>
          Salvar orçamento
        </Button>
      </form>
    </Card>
  );
}
